/**
 * Reglas de importe de una bajada de capital NUEVA (Yago 26-oct-2026; las exige
 * el POST de gf-monorepo #10195):
 * - póliza de una sola garantía: el capital nuevo va en tramos de `capitalStep`;
 * - multigarantía: cada garantía queda con al menos `minTraitCapital`;
 * - el mínimo general (`minCapital`) se mantiene.
 *
 * El contexto de la bajada trae `capitalStep` y `minTraitCapital`. Si todavía no
 * los trae (backend sin #10195), se usan los mismos valores por defecto que el
 * servidor, para que el portal pueda ir delante.
 */
export const DEFAULT_CAPITAL_STEP = 5000;
export const DEFAULT_MIN_TRAIT_CAPITAL = 10000;

const positiveOr = (value: unknown, fallback: number): number =>
  "number" === typeof value && Number.isFinite(value) && value > 0 ? value : fallback;

export const resolveCapitalStep = (value?: number | null): number =>
  positiveOr(value, DEFAULT_CAPITAL_STEP);

export const resolveMinTraitCapital = (value?: number | null): number =>
  positiveOr(value, DEFAULT_MIN_TRAIT_CAPITAL);

/**
 * Tramos que puede elegir una póliza de una sola garantía, del capital actual
 * hacia abajo: múltiplos de `step`, ≥ `minCapital` y < `currentCapital`.
 */
export const capitalSteps = (currentCapital: number, minCapital: number, step: number): number[] => {
  if (step <= 0 || currentCapital <= 0) return [];
  const steps: number[] = [];
  let value = Math.ceil(currentCapital / step) * step - step;
  for (; value >= minCapital && value > 0; value -= step) {
    if (value < currentCapital) steps.push(value);
  }
  return steps;
};

/** Importe como lo escribe el servidor en sus frases: «5.000 €» (number_format con puntos). */
export const formatEuros = (amount: number): string =>
  `${String(Math.trunc(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;

export type TranslateFn = (key: string) => string;

/**
 * Textos nuevos: las mismas frases con las que el servidor rechaza el POST
 * (`enPalabras`). Claves pendientes de crear en `getlife/translations` (ver
 * docs/i18n-pending.md); mientras no lleguen, el literal ES.
 */
export const CAPITAL_RULE_TEXTS = {
  stepRule: {
    key: "newDashboard.modifications.capital.stepRule",
    fallback: "El capital nuevo debe ir en tramos de {step}.",
  },
  traitMinRule: {
    key: "newDashboard.modifications.capital.traitMinRule",
    fallback: "Cada garantía debe quedar con al menos {min}.",
  },
  minRule: {
    key: "newDashboard.modifications.capital.minRule",
    fallback: "El capital nuevo tiene que ser de al menos {min}.",
  },
  // El estado, no el requisito: la póliza ya no puede bajar (misma frase que
  // CapitalReductionRejected::capitalAtMinimum del servidor).
  atMinimum: {
    key: "newDashboard.modifications.capital.atMinimum",
    fallback: "El capital de esta póliza ya está en el mínimo ({min}): no se puede bajar más.",
  },
} as const;

export const ruleText = (
  translate: TranslateFn,
  rule: keyof typeof CAPITAL_RULE_TEXTS,
  values: Record<string, number>
): string => {
  const { key, fallback } = CAPITAL_RULE_TEXTS[rule];
  const translated = translate(key);
  const template = translated && key !== translated ? translated : fallback;
  return Object.entries(values).reduce(
    (text, [name, amount]) => text.replace(`{${name}}`, formatEuros(amount)),
    template
  );
};

/**
 * Si el servidor manda `enPalabras` (frase en castellano), se enseña tal cual,
 * sea cual sea el estado: el 400 del portal, el 422 de otros carriles o el que
 * venga. Sin frase, el texto de siempre.
 */
export const enPalabrasOr = (error: unknown, fallback: string): string => {
  if (!error || "object" !== typeof error) return fallback;
  const { enPalabras } = error as { enPalabras?: unknown };
  if ("string" === typeof enPalabras && "" !== enPalabras.trim()) {
    return enPalabras;
  }
  return fallback;
};
